import { ImportFileLogRequest } from "../entities/import/ImportFileLogRequest";
import { ImportFileLogResponse, LogResponse } from "../entities/import/ImportFileLogResponse";
import { ImportPreviewResponse } from "../entities/import/ImportPreview";
import { ImportLogListResponse } from "../pages/shared/entities/ImportLogListResponse";
import CompanyService from "./CompanyService";
import ExportService from "./ExportService";
import RequestService from "./RequestService";
import { TranslationService } from "./TranslationService";

export class ImportService {
    public static get(id: string): Promise<ImportGetResponse | Error> {
        return RequestService.post("/import/get", { id });
    }
    public static getFile(id: string): Promise<any | Error> {
        return RequestService.post("/import/getfile", { id });
    }
    public static getList(filter: ImportListFilter): Promise<ImportLogListResponse | Error> {
        return RequestService.post("/import/list", { ...filter, additionaldefinitions: CompanyService.getAdditionalDefinitions(), currencies: CompanyService.getCurrencies() });
    }
    public static delete(id: number): Promise<unknown | Error> {
        return RequestService.post("/import/delete", { id });
    }
    public static getImportTypes() {
        return [...ImportTypes];
    }
    public static findImportType(type: number | undefined, updateStrategy: number) {
        let importType = ImportService.getImportTypes().find(x => x.value === type?.toString());
        importType = importType ?? ImportService.getImportTypes().find(x => x.value === (type?.toString() + "-" + updateStrategy.toString()));
        return importType;
    }
    public static set(importtemplateid: number, fileid: string): Promise<{
        async: 0 | 1,
        importid: number,
        processid: number,
    } | Error> {
        return RequestService.post("/import/set", { importtemplateid, fileid });
    }
    public static preview(importid: number): Promise<ImportPreviewResponse | Error> {
        return RequestService.post("/import/preview", { importid });
    }
    public static getStatus(importid: number): Promise<{ percentage: string, status: string, } | Error> {
        return RequestService.post("/import/getstatus", { id: importid });
    }
    public static importData(importid: number): Promise<ImportPreviewResponse | Error> {
        return RequestService.post("/import/importdata", { importid });
    }
    public static runAuto(importTemplateId: number): Promise<ImportPreviewResponse | Error> {
        return RequestService.post("/import/runauto", { id: importTemplateId });
    }
    public static async export(filter: ImportListFilter, count: number) {
        return await ExportService.requestExport("/import/exportrequest", { ...filter, additionaldefinitions: CompanyService.getAdditionalDefinitions(), currencies: CompanyService.getCurrencies() }, count);
    }
    public static fileLog(request: ImportFileLogRequest): Promise<ImportFileLogResponse | Error> {
        return RequestService.post("/import/filelog", request);
    }
    public static getLog(id: string): Promise<LogResponse | Error> {
        return RequestService.post("/import/log", { id });
    }
    public static anyImportVisible(): boolean {
        return (
            !CompanyService.getSetting('hideioimport')
            || !CompanyService.getSetting('hidepersonimport')
            || !CompanyService.getSetting('hidetransactionimport')
            || !CompanyService.getSetting('hideactivityimport')
            || !CompanyService.getSetting('hidenewioimport')
            || !CompanyService.getSetting("hidefileimport")
            || CompanyService.getSetting("enableiodelete")
        )
    }
    public static getComments() {
        return [
            {
                value: 3,
                text: TranslationService.translate.Comments,
                isEnabled: true
            },
        ]
    }
    public static getImports() {
        return [
            { text: TranslationService.translate.OutstandingInvoicesList, value: 0, isEnabled: !CompanyService.getSetting("hideioimport"), },//
            { text: TranslationService.translate.Client, value: 1, isEnabled: !CompanyService.getSetting("hidepersonimport"), },//
            { text: TranslationService.translate.Transaction, value: 2, isEnabled: !CompanyService.getSetting("hidetransactionimport"), },//
            {
                value: 3,
                text: TranslationService.translate.Comments,
                isEnabled: !CompanyService.getSetting("hideactivityimport") || CompanyService.getAuthorization().includes('editimporttemplate'),
            },
            { text: TranslationService.translate.InvoiceFiles, value: 4, isEnabled: !CompanyService.getSetting("hidefileimport"), },
            { text: TranslationService.translate.UpdateMultipleInvoices, value: 5, isEnabled: !CompanyService.getSetting("hideupdateioimport"), },//
            { text: TranslationService.translate.AutomaticInvoiceUpdate, value: 6, isEnabled: CompanyService.getSetting("enableioautomaticupdate"), },
            { text: TranslationService.translate.MassInvoiceDeletion, value: 7, isEnabled: CompanyService.getSetting("enableiodelete"), },
            { text: TranslationService.translate.NewInvoices, value: 8, isEnabled: !CompanyService.getSetting("hidenewioimport"), },//
            { text: TranslationService.translate.AutomaticCustomerUpdate, value: 9, isEnabled: CompanyService.getSetting("enablepersonautomaticupdate"), },
            { text: TranslationService.translate.Currencies, value: 10, isEnabled: false, },
            { text: TranslationService.translate.Dictionary, value: 11, isEnabled: CompanyService.getSetting("allowimportdictionary"), },
            { text: "", value: 12, isEnabled: false, },
        ];
    }
    public static importFinish(id: string): Promise<ImportGetResponse | Error> {
        return RequestService.post("/import/finish", { id });
    }
}

export enum ImportIds {
    OutstandingInvoicesList,
    Client,
    Transaction,
    Comments,
    InvoiceFiles,
    UpdateMultipleInvoices,
    AutomaticInvoiceUpdate,
    MassInvoiceDeletion,
    NewInvoices,
    AutomaticCustomerUpdate,
    Currencies,
    Dictionary,
}

const ImportTypes = [
    { text: 'SyncInvoices', value: '0-0' },
    { text: 'UpdateInvoices', value: '0-2' },
    { text: 'NewInvoices', value: '0-1' },
    { text: 'Customers', value: '1' },
    { text: 'PaymentsAdjustments', value: '2' },
    { text: 'Comments', value: '3' },
    { text: 'NewInvoices', value: '8' },
    { text: 'InvoicesUpdate', value: '5' },
    { text: 'Currencies', value: '10' },
    { text: 'InvoiceAutoUpdates', value: '6' },
    { text: 'CustomerAutoUpdates', value: '9' }
];

type ImportListFilter = {
    from?: string;
    to?: string;
    importTemplateId?: number;
    type?: string;
    filename?: string;
    page: number;
    pagesize: number;
}

export type ImportGetResponse = {
    "Response": string,
    "item": {
        "IOCount": number,
        "PersonCount": number,
        "TransactionCount": number,
        "MessageCount": number,
        "TagCount": number,
        "GroupCount": number,
        "UserName": any,
        "total": number,
        "ImportID": number,
        "UserID": number,
        "Created": any,
        "File": any,
        "ImportTemplateID": number,
        "Percentage": null,
        "Status": any,
        "LastStatus": any,
        "Type": number,
        "UpdateRows": boolean,
        "UpdateStrategy": number,
        "Started": any,
        "FTP": null,
        "Completed": null,
        "SkipControls": null,
        "ControlResult": any,
        "DataResult": null,
        "Filename": any,
        "Paused": null,
        "IOType": any,
        "ProcessRequestID": number,
        "TempFile": null,
        "ImportID_ref": null,
        "Rows": 2,
        "Repeated": null,
        "Errors": null,
        "New": null,
        "Modified": null,
        "NoChanges": null,
        "PreDictionaryRows": null,
        "ErrorFile": null,
        "ErrorReported": null,
        "ZeroAmount": null,
        "rowsOk": null,
        "Recovered": null,
        "Resumed": null,
        "PausedDate": null,
        "ControlSuspended": boolean,
        "ImportTemplate": {
            "ImportTemplateDictionaries": any[],
            "ImportTemplatePredecessors": any[],
            "populateTemplateMehod": number,
            "reopenStrategy": number,
            "calculateMehod": number,
            "HasControls": boolean,
            "ValidateSize": boolean,
            "entity": number,
            "autoPeriod": number,
            "onRepeat": number,
            "Columns": [
                {
                    "ix": any,
                    "name": any,
                    "pattern": any,
                    "Field": number
                },
                {
                    "ix": any,
                    "name": any,
                    "pattern": any,
                    "Field": number
                },
                {
                    "ix": any,
                    "name": any,
                    "pattern": any,
                    "Field": number
                },
                {
                    "ix": any,
                    "name": any,
                    "pattern": any,
                    "Field": number
                }
            ],
            "Client": any,
            "ReferenceNumber": any,
            "Date": any,
            "Amount": any,
            "Currency": any,
            "Group": any,
            "dueDate": any,
            "ImportTemplateID": number,
            "Created": any,
            "CreatorUserID": number,
            "ColumnData": any,
            "IgnoreFirstRow": boolean,
            "Sheet": any,
            "Type": number,
            "Name": any,
            "Entity": number,
            "DateFormat": any,
            "DueDateFormat": any,
            "UpdateIOs": boolean,
            "Separator": any,
            "HasTemplate": boolean,
            "FirstRow": null,
            "EmailReturn": any,
            "OwnerUserID": null,
            "OnErrorContinue": null,
            "PersonKeyWithoutAccents": null,
            "ValidateAmount": boolean,
            "IncludePaid": boolean,
            "NoIOReopen": null,
            "OnRepeat": number,
            "AutoPeriod": null,
            "AutoFrequency": null,
            "CalculatedDate": null,
            "ValidatePeriod": null,
            "ValidateFrequency": null,
            "ValidateHour": null,
            "ValidateEmail": null,
            "exid": null,
            "ValidateSizeMargin": null,
            "AlarmHour": null,
            "AlarmEmail": null,
            "Massive": null,
            "TemplateFolderName": null,
            "UpdateAmount": null,
            "PopulateTemplateMethod": number,
            "ControlNewInvoice": null,
            "ControlNewCustomer": null,
            "ControlNewPayment": number,
            "ControlDebtIncrement": null,
            "ControlDebtDecrement": null,
            "numberCulture": any,
            "CalculateMethod": boolean,
            "LastDataCol": null,
            "DestinationCompanyID": null,
            "DestinationImportTemplateID": null,
            "CleanGroups": boolean,
            "HasQuotedValues": boolean,
            "EmailReturnAdmin": null,
            "OnlyNotifyFails": null,
            "IOFilter": null,
            "FixedColumns": any,
            "CustomPersonIdentityID": null,
            "HasPredecessor": null,
            "IsPredecessor": null,
            "AlarmPeriod": null,
            "AlarmFrequency": null,
            "AutoHour": null,
            "ImportTemplateID_ref": null,
            "NSeparator": null,
            "Encoding": null,
            "BlobName": any,
            "ReOpenStrategy": number,
            "TemplateFirstRow": null,
            "OrderBy": null,
            "processZeroIOs": boolean,
            "C_ColumnData": any,
        },
        "IOs": [],
        "Transactions": [],
        "People": [],
        "Groups": [],
        "Messages": [],
        "Tags": [],
        "ImportStatusLogs": []
    },
    "process": {
        "typeNum": 4,
        "CompanyName": any,
        "UserName": null,
        "isImport": boolean,
        "getShard": number,
        "ProcessRequestID": 2587,
        "Type": 4,
        "UserID": 171,
        "Created": any,
        "Started": any,
        "Completed": null,
        "Email": any,
        "Status": null,
        "Data": any,
        "Data2": any,
        "AdminID": null,
        "When": any,
        "Success": null,
        "Error": null,
        "Processor": null,
        "FilterUserIDPaused": null,
        "Scheduled": null,
        "ScheduledReportID": null,
        "BlobName": null,
        "HasBlobFilter": null,
        "Count": null,
        "LastStatus": null,
        "QA": null,
        "Shard": number,
        "ConnectionString": null,
        "OriginProcessRequestID": null,
        "Version": 1
    }
}